import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from './platformApi';

export const platformKeys = {
  all: ['platform'] as const,
  flags: () => [...platformKeys.all, 'feature-flags'] as const,
  plans: () => [...platformKeys.all, 'plans'] as const,
  agencies: () => [...platformKeys.all, 'agencies'] as const,
  agency: (id: string) => [...platformKeys.all, 'agencies', id] as const,
  commissions: () => [...platformKeys.all, 'commissions'] as const,
  entitlements: () => [...platformKeys.all, 'entitlements', 'me'] as const,
  myUsage: () => [...platformKeys.all, 'usage', 'me'] as const,
};

export function useFeatureFlags() {
  return useQuery({
    queryKey: platformKeys.flags(),
    queryFn: api.listFeatureFlags,
  });
}

export function useUpdateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: api.FeatureFlagUpdate }) =>
      api.updateFeatureFlag(id, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: platformKeys.flags() });
    },
  });
}

export function usePlans(tenantType = 'school') {
  return useQuery({
    queryKey: [...platformKeys.plans(), tenantType],
    queryFn: () => api.listPlans(tenantType),
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createPlan,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: platformKeys.plans() });
    },
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: api.PlatformPlanUpdate }) =>
      api.updatePlan(id, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: platformKeys.plans() });
    },
  });
}

export function useSchoolUsage(schoolId: string, enabled = true) {
  return useQuery({
    queryKey: [...platformKeys.all, 'usage', schoolId],
    queryFn: () => api.getSchoolUsage(schoolId),
    enabled: Boolean(schoolId) && enabled,
  });
}

export function useAgencies(enabled = true) {
  return useQuery({
    queryKey: platformKeys.agencies(),
    queryFn: api.listAgencies,
    enabled,
  });
}

export function useAgency(id: string, enabled = true) {
  return useQuery({
    queryKey: platformKeys.agency(id),
    queryFn: () => api.getAgency(id),
    enabled: Boolean(id) && enabled,
  });
}

export function useCreateAgency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createAgency,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: platformKeys.agencies() });
    },
  });
}

export function useUpdateAgency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: api.AgencyUpdate }) =>
      api.updateAgency(id, body),
    onSuccess: async (_data, vars) => {
      await qc.invalidateQueries({ queryKey: platformKeys.agencies() });
      await qc.invalidateQueries({ queryKey: platformKeys.agency(vars.id) });
    },
  });
}

export function useCommissions() {
  return useQuery({
    queryKey: platformKeys.commissions(),
    queryFn: () => api.listCommissions(),
  });
}

async function invalidateCommercials(qc: ReturnType<typeof useQueryClient>) {
  await qc.invalidateQueries({ queryKey: platformKeys.commissions() });
  await qc.invalidateQueries({ queryKey: platformKeys.agencies() });
  await qc.invalidateQueries({ queryKey: ['university'] });
}

export function useCreateCommission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createCommission,
    onSuccess: async () => {
      await invalidateCommercials(qc);
    },
  });
}

export function useUpdateCommission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: api.CommissionRuleUpdate }) =>
      api.updateCommission(id, body),
    onSuccess: async () => {
      await invalidateCommercials(qc);
    },
  });
}

export function useDeleteCommission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteCommission,
    onSuccess: async () => {
      await invalidateCommercials(qc);
    },
  });
}

export function useBillingCheckout() {
  return useMutation({
    mutationFn: api.createBillingCheckout,
  });
}

export function useUpdateWhiteLabel(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: api.WhiteLabelUpdate) => api.updateWhiteLabel(schoolId, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'schools'] });
    },
  });
}

export function useApiKeys(schoolId: string, enabled = true) {
  return useQuery({
    queryKey: [...platformKeys.all, 'api-keys', schoolId],
    queryFn: () => api.listApiKeys(schoolId),
    enabled: Boolean(schoolId) && enabled,
  });
}

export function useCreateApiKey(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.createApiKey({ school_id: schoolId, name }),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: [...platformKeys.all, 'api-keys', schoolId],
      });
    },
  });
}

export function useRevokeApiKey(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => api.revokeApiKey(keyId, schoolId),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: [...platformKeys.all, 'api-keys', schoolId],
      });
    },
  });
}

export function useAgencyAgents(agencyId: string | null) {
  return useQuery({
    queryKey: [...platformKeys.all, 'agency-agents', agencyId],
    queryFn: () => api.listAgencyAgents(agencyId!),
    enabled: Boolean(agencyId),
  });
}

export function useAssignAgencyAgent(agencyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: api.AgencyAgentCreate) => api.assignAgencyAgent(agencyId, body),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: [...platformKeys.all, 'agency-agents', agencyId],
      });
      await qc.invalidateQueries({ queryKey: platformKeys.agency(agencyId) });
    },
  });
}

export function useRemoveAgencyAgent(agencyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => api.removeAgencyAgent(agencyId, agentId),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: [...platformKeys.all, 'agency-agents', agencyId],
      });
      await qc.invalidateQueries({ queryKey: platformKeys.agency(agencyId) });
    },
  });
}

const ENTITLEMENT_ROLES = new Set([
  'school_admin',
  'teacher',
  'counselor',
  'parent',
  'student',
  'agent',
]);

export function useMyEntitlements(enabled = true) {
  return useQuery({
    queryKey: platformKeys.entitlements(),
    queryFn: api.getMyEntitlements,
    enabled,
    staleTime: 60_000,
  });
}

export function useMyPlanUsage(enabled = true) {
  return useQuery({
    queryKey: platformKeys.myUsage(),
    queryFn: api.getMyPlanUsage,
    enabled,
    staleTime: 15_000,
  });
}

export function useInvalidateMyPlanUsage() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: platformKeys.myUsage() });
}

export function useEntitlementModules(role: string | null | undefined, schoolId?: string | null) {
  const needs = Boolean(role && ENTITLEMENT_ROLES.has(role) && schoolId);
  const query = useMyEntitlements(needs);
  if (!needs) return { modules: null as string[] | null, isLoading: false };
  if (query.isLoading || !query.data) {
    // Hide gated items until loaded (safer than flashing unavailable modules)
    return { modules: [] as string[], isLoading: true };
  }
  return { modules: query.data.modules, isLoading: false };
}
